import { Analytics } from '@cobliteam/analytics';
import type { Insight } from '@cobliteam/api-lib/src/__generated__/graphql';
import Badge from '@cobliteam/ui/components/Badge';
import Typography from '@cobliteam/ui/components/Typography';
import { endOfWeek, format, startOfWeek, subDays } from '@cobliteam/utils/date';
import { insightUtils } from '../..';

interface WeekInsightItemProps {
  insight: Insight;
  isSelected: boolean;
  onInsightClick: () => void;
  wasInsightSeen: boolean;
}

const WeekInsightItem = ({ insight, isSelected, onInsightClick, wasInsightSeen }: WeekInsightItemProps) => {
  if (!insight.impact) return null;

  const bgColor = wasInsightSeen ? (isSelected ? 'bg-gray-4' : 'hover:bg-gray-4') : isSelected ? 'bg-primary-6' : 'bg-primary-light hover:bg-primary-6';

  return (
    <div className={`relative flex flex-row items-center rounded-sm px-3 py-2 last:mb-6 ${bgColor} cursor-pointer`} onClick={onInsightClick}>
      <Badge color='primary' variant='dot' position='top-right' hide={isSelected || wasInsightSeen}>
        <Typography className='line-clamp-1 text-gray-11' variant='text-md-semibold'>
          {insight.impact}
        </Typography>
      </Badge>
    </div>
  );
};

export interface WeekInsightsProps {
  insights: Insight[];
  selectedInsight?: Insight;
  onInsightClick: (insight: Insight) => void;
  readInsights?: { [id: string]: string };
}

const WeekInsights = ({ insights, selectedInsight, onInsightClick, readInsights = {} }: WeekInsightsProps) => {
  const { getInsightType } = insightUtils();
  const isInsightRead = (insightId: string) => {
    return Boolean(readInsights[insightId]);
  };

  const handleInsightClick = (insight: Insight) => {
    Analytics.track('Insight Modal List Item Clicked', { id: insight.id, type: getInsightType(insight), category: insight.category });
    onInsightClick(insight);
  };

  const renderInsight = (insight: Insight) => (
    <WeekInsightItem
      key={insight.id}
      insight={insight}
      isSelected={Boolean(selectedInsight && insight.id === selectedInsight.id)}
      onInsightClick={() => handleInsightClick(insight)}
      wasInsightSeen={isInsightRead(insight.id)}
    />
  );

  if (insights.length === 0) return null;

  const dayBeforeInsightGeneration = subDays(new Date(insights[0].createdAt), 1);
  const weekStart = startOfWeek(dayBeforeInsightGeneration);
  const weekEnd = endOfWeek(dayBeforeInsightGeneration);

  return (
    <>
      <Typography className='mb-3' variant='caption-default' color='secondary'>
        Semana {format(weekStart, 'dd/MM/yy')} - {format(weekEnd, 'dd/MM/yy')}
      </Typography>
      <div className='flex flex-col gap-3'>{insights?.map(renderInsight)}</div>
    </>
  );
};

export default WeekInsights;
